import React, { useState, useEffect } from 'react';
import { db } from '../firebase';
import { collection, addDoc, query, orderBy, onSnapshot, serverTimestamp, updateDoc, doc, deleteDoc, increment } from 'firebase/firestore';
import styles from './FeedbackPage.module.css';
import { useUser } from '../context/UserContext';

const FeedbackPage = () => {
    const [feedbacks, setFeedbacks] = useState([]);
    const [newFeedback, setNewFeedback] = useState('');
    const [loading, setLoading] = useState(true);
    const [posting, setPosting] = useState(false);
    const [feedbackSent, setFeedbackSent] = useState(false);
    const [submitDisabled, setSubmitDisabled] = useState(false);
    const { currentUser } = useUser();
    const isAdmin = currentUser?.admin || currentUser?.editor;

    // Use localStorage to track liked feedback IDs to prevent multiple likes from same client
    const [likedIds, setLikedIds] = useState(() => {
        try {
            const saved = localStorage.getItem('likedFeedbacks');
            return saved ? JSON.parse(saved) : [];
        } catch (e) {
            return [];
        }
    });

    useEffect(() => {
        const q = query(collection(db, 'feedbacks'), orderBy('likes', 'desc'));
        const unsubscribe = onSnapshot(q, (snapshot) => {
            const items = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
            setFeedbacks(items);
            setLoading(false);
        });
        return () => unsubscribe();
    }, []);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!newFeedback.trim()) return;

        setPosting(true);
        try {
            await addDoc(collection(db, 'feedbacks'), {
                text: newFeedback.trim(),
                likes: 0,
                timestamp: serverTimestamp(),
                solved: false
            });
            setNewFeedback('');
            setFeedbackSent(true);
            setSubmitDisabled(true);
            setTimeout(() => {
                setFeedbackSent(false);
                setSubmitDisabled(false);
            }, 10000);
        } catch (error) {
            console.error("Error adding feedback:", error);
            alert("Failed to submit feedback.");
        } finally {
            setPosting(false);
        }
    };

    const handleLike = async (feedbackId) => {
        if (likedIds.includes(feedbackId)) return;

        try {
            const feedbackRef = doc(db, 'feedbacks', feedbackId);
            await updateDoc(feedbackRef, {
                likes: increment(1)
            });

            const newLikedIds = [...likedIds, feedbackId];
            setLikedIds(newLikedIds);
            localStorage.setItem('likedFeedbacks', JSON.stringify(newLikedIds));
        } catch (error) {
            console.error("Error liking feedback", error);
        }
    };

    const handleDelete = async (feedbackId) => {
        if (!window.confirm("Delete this feedback?")) return;
        try {
            await deleteDoc(doc(db, 'feedbacks', feedbackId));
        } catch (error) {
            console.error("Error deleting feedback:", error);
        }
    };

    const handleSolve = async (feedbackId, currentStatus) => {
        try {
            await updateDoc(doc(db, 'feedbacks', feedbackId), {
                solved: !currentStatus
            });
        } catch (error) {
            console.error("Error updating status:", error);
        }
    };

    return (
        <div className={styles.container}>
            <h1>Anonymous Feedback</h1>
            <p className={styles.subtitle}>Help us improve PeaksOnFoot. Your feedback is anonymous.</p>

            <form onSubmit={handleSubmit} className={styles.form}>
                <textarea
                    className={styles.textarea}
                    value={newFeedback}
                    onChange={(e) => setNewFeedback(e.target.value)}
                    placeholder="Write your feedback here..."
                    maxLength={1000}
                    required
                />
                <button type="submit" className={styles.submitBtn} disabled={posting || submitDisabled}>
                    {posting ? 'Submitting...' : 'Submit Feedback'}
                </button>
                {feedbackSent && (
                    <div className={styles.feedbackSent}>Feedback sent! Thank you</div>
                )}
            </form>

            <div className={styles.feedbacksList}>
                {loading ? <p>Loading feedback...</p> : feedbacks.map(item => (
                    <div key={item.id} className={`${styles.card} ${item.solved ? styles.solved : ''}`}>
                        <div className={styles.cardContent}>
                            <p className={styles.feedbackText}>{item.text}</p>
                            {item.solved && <span className={styles.solvedBadge}>Solved</span>}
                        </div>
                        <div className={styles.actions}>
                             <button
                                onClick={() => handleLike(item.id)}
                                className={`${styles.likeBtn} ${likedIds.includes(item.id) ? styles.liked : ''}`}
                                disabled={likedIds.includes(item.id)}
                             >
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                                    <path d="M2 10.5a1.5 1.5 0 113 0v6a1.5 1.5 0 01-3 0v-6zM6 10.333v5.43a2 2 0 001.106 1.79l.05.025A4 4 0 008.943 18h5.416a2 2 0 001.962-1.608l1.2-6A2 2 0 0015.56 8H12V4a2 2 0 00-2-2 1 1 0 00-1 1v.667a4 4 0 01-.8 2.4L6.8 7.933a4 4 0 00-.8 2.4z" />
                                </svg>
                                {item.likes || 0}
                             </button>

                             {isAdmin && (
                                <div className={styles.adminActions}>
                                    <button onClick={() => handleSolve(item.id, item.solved)} className={styles.solveBtn}>
                                        {item.solved ? 'Mark Unsolved' : 'Mark Solved'}
                                    </button>
                                    <button onClick={() => handleDelete(item.id)} className={styles.deleteBtn}>
                                        Delete
                                    </button>
                                </div>
                             )}
                        </div>
                    </div>
                ))}
                {!loading && feedbacks.length === 0 && (
                    <p className={styles.empty}>No feedback yet. Be the first!</p>
                )}
            </div>
        </div>
    );
};

export default FeedbackPage;
